import { BASE_URL } from "../lib/api";
import { CodeBlock } from "./kit";

const BASE = BASE_URL;

const curl = `curl -X POST ${BASE}/api/v1/emails \\
  -H "Authorization: Bearer cm_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-1234-receipt" \\
  -d '{
    "from": "Acme <receipts@your-verified-domain.com>",
    "to": ["customer@example.com"],
    "subject": "Your receipt",
    "html": "<p>Thanks for your order.</p>",
    "text": "Thanks for your order.",
    "tags": [{"name": "category", "value": "receipt"}]
  }'`;

const node = `const res = await fetch("${BASE}/api/v1/emails", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.CARIO_API_KEY}\`,
    "Content-Type": "application/json",
    "Idempotency-Key": "order-1234-receipt",
  },
  body: JSON.stringify({
    from: "Acme <receipts@your-verified-domain.com>",
    to: ["customer@example.com"],
    subject: "Your receipt",
    html: "<p>Thanks for your order.</p>",
  }),
});
const email = await res.json(); // { id, object: "email", status: "queued", test_mode }`;

const getOne = `curl ${BASE}/api/v1/emails/msg_xxx \\
  -H "Authorization: Bearer cm_live_YOUR_KEY"`;

const listAll = `curl "${BASE}/api/v1/emails?limit=20&before=msg_xxx" \\
  -H "Authorization: Bearer cm_live_YOUR_KEY"`;

const verifyNode = `import crypto from "node:crypto";

// headers: webhook-id, webhook-timestamp, webhook-signature ("v1,<base64>")
function verify(secret, id, ts, rawBody, signatureHeader) {
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false; // replay window
  const key = Buffer.from(secret.replace("whsec_", ""), "base64");
  const expected = "v1," + crypto.createHmac("sha256", key).update(\`\${id}.\${ts}.\${rawBody}\`).digest("base64");
  return signatureHeader.split(" ").some((s) =>
    s.length === expected.length && crypto.timingSafeEqual(Buffer.from(s), Buffer.from(expected)));
}`;

const FIELDS = [
  ["from", "string", "Sender. The domain must be verified for your workspace."],
  ["to / cc / bcc", "string | string[]", "Recipients. Max 50 in total. Suppressed addresses are skipped."],
  ["reply_to", "string | string[]", "Optional reply-to addresses."],
  ["subject", "string", "Required unless template_id is used."],
  ["html / text", "string", "At least one is required (max 512 KB combined)."],
  ["tags", "{name, value}[]", "Up to 10 tags, [A-Za-z0-9_-]."],
  ["idempotency_key", "string", "Or the Idempotency-Key header. Same key + same body returns the original email."],
  ["attachments", "array", "Up to 10: {\"file_id\": \"file_…\"} from an upload, or {\"filename\", \"content\" (base64), \"content_type\"} for small inline files. 10 MB per file, 20 MB per email."],
  ["template_id / variables", "string / object", "Render a saved template; variables are HTML-escaped."],
  ["category", "transactional | subscription", "Every email gets List-Unsubscribe headers (one-click when there is a single recipient). subscription requires exactly one recipient."],
  ["track_opens / track_clicks", "boolean", "Optional. Overrides the sending domain's tracking setting for this email. Both are off unless turned on."],
];

const contactsCode = `# add or update a contact (custom fields become {{variables}})
curl -X POST ${BASE}/api/v1/audiences/aud_123/contacts \\
  -H "Authorization: Bearer cm_live_..." -H "Content-Type: application/json" \\
  -d '{"email":"ada@example.com","first_name":"Ada","data":{"plan":"pro"}}'

# list (50 per page, ?q= searches by email)
curl ${BASE}/api/v1/audiences/aud_123/contacts?page=1 -H "Authorization: Bearer cm_live_..."

# remove
curl -X DELETE ${BASE}/api/v1/audiences/aud_123/contacts/ada@example.com -H "Authorization: Bearer cm_live_..."`;

const STATUSES = [
  ["queued", "Stored and waiting for the sending worker."],
  ["retrying", "A temporary (4xx / network) failure; retried with exponential backoff."],
  ["accepted", "The upstream SMTP server returned 250. This is NOT proof of inbox delivery."],
  ["delivered / bounced / complained", "Only set from real Amazon SES events, when event publishing is configured."],
  ["failed", "Permanent rejection, or a send interrupted mid-attempt (never resent to avoid duplicates)."],
  ["simulated", "Sent with a cm_test_ key. Nothing left the platform."],
];

export default function DocsContent() {
  return (
    <div className="space-y-12" data-testid="docs-content">
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Authentication</h2>
        <p className="text-sm text-zinc-400">Create an API key under <b className="text-zinc-200">API keys</b>. Send it as <code className="text-purple-300">Authorization: Bearer cm_live_…</code>.
          Keys are shown once and stored only as a hash. <code className="text-purple-300">cm_test_</code> keys run every validation but never send email.</p>
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Send an email — <span className="font-mono text-base text-purple-300">POST /api/v1/emails</span></h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <CodeBlock code={curl} label="curl" testId="docs-curl" />
          <CodeBlock code={node} label="node.js" testId="docs-node" />
        </div>
        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Field</th><th className="p-3">Type</th><th className="p-3">Notes</th></tr></thead>
            <tbody>{FIELDS.map(([f, ty, n]) => (
              <tr key={f} className="border-t border-white/5"><td className="p-3 font-mono text-xs text-purple-200">{f}</td><td className="p-3 font-mono text-xs text-zinc-400">{ty}</td><td className="p-3 text-zinc-300">{n}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Attachments — <span className="font-mono text-base text-purple-300">POST /api/v1/uploads</span></h2>
        <p className="text-sm text-zinc-400">Requests are limited to 1 MB, so larger files are uploaded in pieces with the same API key: start with <code className="text-purple-300">{'{"filename", "content_type", "size", "kind": "attachment"}'}</code>, send each piece as raw bytes to <code className="text-purple-300">PUT /api/v1/uploads/:id/chunks/:index</code> (the start response gives <code className="text-purple-300">chunk_size</code>), then call <code className="text-purple-300">POST /api/v1/uploads/:id/complete</code> to get a <code className="text-purple-300">file_id</code>. Executable files (.exe, .js, .bat …) are refused. Every email is also screened automatically for phishing, spam and malware patterns; flagged messages are still sent and are reviewed by our trust &amp; safety team.</p>
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Retrieve and list</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <CodeBlock code={getOne} label="GET /api/v1/emails/:id" testId="docs-get" />
          <CodeBlock code={listAll} label="GET /api/v1/emails" testId="docs-list" />
        </div>
      </section>
      <section className="space-y-4" data-testid="docs-contacts">
        <h2 className="text-xl font-semibold text-white">Contacts — <span className="font-mono text-base text-purple-300">/api/v1/audiences</span></h2>
        <p className="text-sm text-zinc-400">Keep your campaign audiences in sync from your own system. Keys need the <code className="text-purple-300">contacts:read</code> or <code className="text-purple-300">contacts:write</code> scope. Create audiences in the dashboard under <b className="text-zinc-200">Contacts</b>; their ids are shown there and returned by <code className="text-purple-300">GET /api/v1/audiences</code>. Adding an existing email updates it. Only add people who agreed to receive email from you.</p>
        <CodeBlock code={contactsCode} label="contacts" testId="docs-contacts-code" />
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Message statuses</h2>
        <ul className="divide-y divide-white/5 rounded-lg border border-white/10">
          {STATUSES.map(([s, d]) => (
            <li key={s} className="flex flex-col gap-1 p-3 sm:flex-row sm:gap-6"><span className="w-64 shrink-0 font-mono text-xs text-purple-200">{s}</span><span className="text-sm text-zinc-300">{d}</span></li>
          ))}
        </ul>
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Errors and limits</h2>
        <p className="text-sm text-zinc-400">401 invalid/revoked key · 403 missing scope, unverified sender domain or suspended workspace · 409 idempotency conflict · 413 too large · 422 validation or all recipients suppressed · 429 per-minute, daily or monthly limit · 503 sending paused or not configured. Every response carries an <code className="text-purple-300">X-Request-ID</code>.</p>
      </section>
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Webhook signatures</h2>
        <p className="text-sm text-zinc-400">Each delivery is an HTTPS POST signed with HMAC-SHA256 over <code className="text-purple-300">{"{webhook-id}.{webhook-timestamp}.{body}"}</code>. Reject requests older than 5 minutes.</p>
        <CodeBlock code={verifyNode} label="verify (node.js)" testId="docs-webhook-verify" />
      </section>
      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-white">SMTP</h2>
        <p className="text-sm text-zinc-400">The CARIO SMTP gateway accepts mail on port 587 (STARTTLS required, then AUTH PLAIN or LOGIN). Username: <code className="text-purple-300">cario</code>, password: an API key with the <code className="text-purple-300">emails:send</code> scope. Messages pass through the same checks, quotas, suppressions and queue as the REST API. Add <code className="text-purple-300">X-Cario-Tags: key=value,key2=value2</code> to tag a message (letters, digits, _ and -; other characters become _). Attachments are supported (up to 10 per message).</p>
        <p className="text-xs text-amber-300">Availability: the gateway runs on our server, but its host and port are shown in Settings only once a public TCP endpoint is published. Until then, use the REST API.</p>
      </section>
    </div>
  );
}
